// src/knowledge/AddKnowledgeDialog.tsx
import * as React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
  Stack,
  LinearProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
} from "@mui/material";
import {
  DataSource,
  User,
  UserKnowledge,
  AddUserKnowledgeInput,
} from "../../models/API";
import { addUserKnowledge, syncKnowledge } from "../../graphql/mutations";
import { generateClient } from "aws-amplify/api";

type Mode = "add" | "edit";

type Props = {
  open: boolean;
  mode?: Mode;
  currentUserId: number;
  currentTeamId: number;
  initial?: Partial<UserKnowledge>; // when editing, pass the row
  userConfig: User;
  effectiveRole: string;
  folderId?: number | null;
  onClose: () => void;
  onCreate?: (
    ds: Omit<DataSource, "id" | "createdAt" | "status"> & { blob?: File }
  ) => Promise<void> | void;
  onUpdate?: (
    id: string,
    ds: Partial<DataSource> & { blob?: File | null }
  ) => Promise<void> | void;
};

export default function AddKnowledgeDialog({
  open,
  mode = "add",
  currentUserId,
  currentTeamId,
  initial,
  userConfig,
  effectiveRole,
  folderId,
  onClose,
  onCreate,
  onUpdate,
}: Props) {
  const isEdit = mode === "edit";
  const [tab, setTab] = React.useState<string>(
    initial?.knowledge_type ?? "TEXT"
  );
  const [id, setId] = React.useState(initial?.id ?? 0);
  const [name, setName] = React.useState(initial?.knowledge_name ?? "");
  const [isPublic, setIsPublic] = React.useState<boolean>(
    initial?.is_public ?? false
  );
  const [isGlobal, setIsGlobal] = React.useState<boolean>(
    initial?.is_global ?? false
  );
  const [dataType, setDataType] = React.useState<string>(
    initial?.knowledge_type ?? "TEXT"
  );
  const [description, setDescription] = React.useState(
    initial?.description ?? ""
  );
  const [content, setContent] = React.useState(initial?.content ?? "");
  const [skillVersion, setSkillVersion] = React.useState("1.0.0");
  const [skillTags, setSkillTags] = React.useState("");

  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const client = generateClient();

  React.useEffect(() => {
    console.log("---------------------------");
    console.log("initial", initial);
    console.log("---------------------------");
    setId(initial?.id ?? 0);
    setName(initial?.knowledge_name ?? "");
    setDescription(initial?.description ?? "");
    setContent(initial?.content ?? "");
    setTab(initial?.knowledge_type ?? "TEXT");
    setIsPublic(initial?.is_public ?? false);
    setIsGlobal(initial?.is_global ?? false);
    setDataType(initial?.knowledge_type ?? "TEXT");
    // Initialize skill fields from attributes if editing a skill
    if (initial?.knowledge_type === "skill" && initial?.attributes) {
      try {
        const attrs = JSON.parse(initial.attributes);
        setSkillVersion(attrs.version ?? "1.0.0");
        setSkillTags((attrs.tags ?? []).join(", "));
      } catch {
        setSkillVersion("1.0.0");
        setSkillTags("");
      }
    } else {
      setSkillVersion("1.0.0");
      setSkillTags("");
    }
  }, [initial, userConfig, currentTeamId]);

  const reset = () => {
    setId(0);
    setTab("TEXT");
    setName("");
    setIsPublic(false);
    setDescription("");
    setContent("");
    setSkillVersion("1.0.0");
    setSkillTags("");
    setSubmitting(false);
    setError(null);
  };

  const validate = (): string | null => {
    if (!name.trim()) return "Please enter a name.";
    if (!description.trim()) return "Please provide a short description.";
    if (!content.trim()) return "Please enter content.";
    return null;
  };

  const handleSubmit = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      const isSkill = tab === "skill";
      const knowledgeType = isSkill ? "skill" : dataType;
      const attributes = isSkill
        ? JSON.stringify({
            version: skillVersion.trim() || "1.0.0",
            tags: skillTags
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean),
            references: [],
          })
        : JSON.stringify({});

      var addUserKnowledgeInput: AddUserKnowledgeInput;
      if (id === 0) {
        // add
        addUserKnowledgeInput = {
          knowledge_name: name,
          knowledge_type: knowledgeType,
          is_public: isPublic,
          description: description,
          content: content,
          attributes: attributes,
          user_id: currentUserId,
          team_id: currentTeamId,
          folder_id: folderId ?? null,
        };
      } else {
        // edit
        addUserKnowledgeInput = {
          id: id,
          knowledge_name: name,
          knowledge_type: knowledgeType,
          is_public: isPublic,
          description: description,
          content: content,
          attributes: attributes,
          user_id: currentUserId,
          team_id: currentTeamId,
        };
      }
      await client.graphql({
        query: addUserKnowledge,
        variables: {
          input: addUserKnowledgeInput,
        },
      });

      // Trigger Bedrock KB sync asynchronously
      try {
        await client.graphql({
          query: syncKnowledge,
          variables: {
            input: {
              knowledge_id: addUserKnowledgeInput.id || null,
              user_id: currentUserId,
              team_id: currentTeamId,
              action: "upsert",
            },
          },
        });
      } catch (syncErr) {
        console.warn("Knowledge sync trigger failed (non-blocking):", syncErr);
      }

      setError(null);
      setSubmitting(false);
      onClose();
      reset();
    } catch (e: any) {
      console.error(e);
      setError(e.message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={
        submitting
          ? undefined
          : () => {
              onClose();
              reset();
            }
      }
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>{isEdit ? "Edit knowledge" : "Add knowledge"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {/* ID Field - Only shown in edit mode, always disabled */}
          {isEdit && (
            <TextField
              label="ID"
              value={id}
              fullWidth
              disabled
              helperText="Knowledge ID (read-only)"
            />
          )}

          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <RadioGroup
            row
            value={isPublic ? "PUBLIC" : "PRIVATE"}
            onChange={(_, v) => setIsPublic("PUBLIC" === v)}
            aria-label="visibility"
          >
            {effectiveRole === "user" ? (
              <Tooltip
                title={"Only Admin/Operator users can make knowledge public"}
              >
                <FormControlLabel
                  value="PUBLIC"
                  control={<Radio disabled />}
                  label="Public"
                />
              </Tooltip>
            ) : (
              <FormControlLabel
                value="PUBLIC"
                control={<Radio />}
                label="Public"
              />
            )}
            <FormControlLabel
              value="PRIVATE"
              control={<Radio disabled={effectiveRole === "user" && isEdit} />}
              label="Private"
            />
          </RadioGroup>
          <TextField
            label="Short description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            required
            minRows={1}
            helperText="Short description"
          />

          {!isEdit && effectiveRole !== "user" && (
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              aria-label="knowledge types"
            >
              <Tab value="TEXT" label="TEXT" />
              <Tab value="skill" label="SKILL" />
            </Tabs>
          )}
          {!isEdit && effectiveRole === "user" && (
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              aria-label="knowledge types"
            >
              <Tab value="TEXT" label="TEXT" />
              <Tab value="skill" label="SKILL" />
            </Tabs>
          )}
          {isEdit && (dataType === "TEXT" || dataType === "skill") && (
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              aria-label="knowledge types"
            >
              <Tab
                value={dataType}
                label={dataType === "skill" ? "SKILL" : "TEXT"}
              />
            </Tabs>
          )}

          {tab === "TEXT" && (
            <Stack spacing={2}>
              <TextField
                label="Content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                fullWidth
                required
                multiline
                minRows={10}
                helperText="Knowledge content"
              />
            </Stack>
          )}

          {tab === "skill" && (
            <Stack spacing={2}>
              <TextField
                label="SKILL.md Instructions"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                fullWidth
                required
                multiline
                minRows={10}
                helperText="Instruction skill content (SKILL.md format)"
                placeholder="Enter skill instructions that guide AI behavior..."
              />
              <TextField
                label="Version"
                value={skillVersion}
                onChange={(e) => setSkillVersion(e.target.value)}
                fullWidth
                helperText="Skill version (e.g., 1.0.0)"
              />
              <TextField
                label="Tags"
                value={skillTags}
                onChange={(e) => setSkillTags(e.target.value)}
                fullWidth
                helperText="Comma-separated tags (e.g., wbr, analysis, metrics)"
              />
            </Stack>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {submitting && <LinearProgress />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onClose();
            reset();
          }}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            submitting ||
            // Regular user: can only edit non-public, non-global
            (effectiveRole === "user" && (isPublic || isGlobal)) ||
            // Global resources can only be edited by FINN team (team_id === 1)
            (isGlobal && currentTeamId !== 1)
          }
        >
          {isEdit ? "Save changes" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
